// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type BaseQueryEnhancer, type FetchArgs } from '@reduxjs/toolkit/query';
import { normalizeFetchArgs, serializeQueryParams } from './normalize-fetch-args';

/** Options for configuring request normalization. */
export interface NormalizeQueryOptions {
  /**
   * Serializer used to decide whether query params are empty.
   *
   * Defaults to {@link serializeQueryParams}, which drops `null`, `undefined`, and
   * other empty values and warns so the endpoint can be corrected.
   */
  paramsSerializer?: (params: Record<string, any>) => string;
}

/**
 * Enhances a base query so the request URL is normalized before it is sent.
 *
 * @remarks
 * Missing trailing slashes are added, with a console warning. Empty query params
 * (`null`, `undefined`, blank strings, `NaN`, empty arrays) are removed so the URL
 * does not end with `?`. Place this enhancer closest to `fetchBaseQuery` so it sees
 * the final args, including params added by other enhancers.
 * @param baseQuery - The base query function to enhance.
 * @param config - Default serializer for query params.
 * @returns A function that executes the enhanced query.
 */
export const normalizeQuery: BaseQueryEnhancer<
  string | FetchArgs,
  NormalizeQueryOptions,
  void | NormalizeQueryOptions
> =
  (baseQuery, config) =>
  async (args, api, extraOptions): Promise<any> => {
    const paramsSerializer =
      extraOptions?.paramsSerializer ??
      config?.paramsSerializer ??
      serializeQueryParams;

    return baseQuery(
      normalizeFetchArgs(args, paramsSerializer, {
        endpoint: api.endpoint,
        type: api.type,
      }),
      api,
      extraOptions,
    );
  };
